import { InfoOutlined } from "@mui/icons-material";
import { Box, Divider, ThemeProvider, Typography } from "@mui/material";
import { FC, ReactNode } from "react";
import logo from '../../images/logo.png';
import { lightTheme } from "../complaint-form/ComplaintForm";
import './PrivacyPolicy.scss';

const CONTROLLER_NAME = 'Inovação Engenharia';
const DPO_EMAIL = 'contato@inovação.seg.br';
const LAST_UPDATE = '27 de julho de 2026';

type Section = {
	title: string;
	content: ReactNode;
};

const sections: Section[] = [
	{
		title: 'Quem é o controlador dos seus dados',
		content: (
			<p>
				Esta plataforma é operada por {CONTROLLER_NAME}, que atua como controladora dos dados pessoais
				aqui tratados, nos termos da Lei nº 13.709/2018 (Lei Geral de Proteção de Dados — LGPD).
				O tratamento ocorre sempre em nome da empresa contratante responsável pelo seu processo
				de admissão, que define as informações exigidas.
			</p>
		),
	},
	{
		title: 'Quais dados tratamos',
		content: (
			<>
				<p>Coletamos apenas os dados necessários ao processo admissional, entre eles:</p>
				<ul>
					<li><b>Dados de identificação:</b> nome, data de nascimento, sexo, CPF e RG;</li>
					<li><b>Dados funcionais:</b> cargo, função, setor, matrícula, data de admissão, CTPS e NIT;</li>
					<li><b>Documentos e arquivos:</b> cópias digitais dos documentos exigidos para a contratação;</li>
					<li><b>Dados de saúde ocupacional:</b> resultados de exames admissionais, como a audiometria.</li>
				</ul>
				<p>
					Não solicitamos dados que não tenham relação direta com a admissão e não utilizamos suas
					informações para publicidade ou perfilamento comercial.
				</p>
			</>
		),
	},
	{
		title: 'Para que utilizamos seus dados',
		content: (
			<>
				<p>
					Os dados e arquivos enviados são utilizados exclusivamente para <b>fins admissionais</b>:
					conferência e validação dos documentos, conformidade das informações cadastrais, agendamento
					e registro de exames ocupacionais e formalização da contratação.
				</p>
				<p>
					Encerrado esse propósito, os dados deixam de ser utilizados para qualquer outra finalidade.
				</p>
			</>
		),
	},
	{
		title: 'Bases legais do tratamento',
		content: (
			<>
				<p>O tratamento se apoia nas seguintes hipóteses legais:</p>
				<ul>
					<li>cumprimento de obrigação legal ou regulatória (art. 7º, II), como a legislação trabalhista e previdenciária;</li>
					<li>execução de contrato ou de procedimentos preliminares à contratação (art. 7º, V);</li>
					<li>exercício regular de direitos (art. 7º, VI);</li>
					<li>para dados de saúde, tutela da saúde e cumprimento de obrigação de medicina do trabalho (art. 11, II).</li>
				</ul>
			</>
		),
	},
	{
		title: 'Por quanto tempo guardamos',
		content: (
			<>
				<p>
					O tratamento é feito <b>por tempo limitado</b>. Os documentos e arquivos enviados são mantidos
					apenas pelo período necessário à conclusão e auditoria do processo admissional e, depois disso,
					são eliminados de forma segura.
				</p>
				<p>
					A única exceção são as informações cuja guarda seja exigida por obrigação legal, regulatória ou
					para o exercício regular de direitos — nesses casos, os dados são retidos somente pelo prazo
					previsto na legislação aplicável e, ao final, descartados.
				</p>
			</>
		),
	},
	{
		title: 'Com quem compartilhamos',
		content: (
			<p>
				Seus dados podem ser compartilhados com a empresa contratante responsável pela sua admissão, com
				clínicas e profissionais de saúde ocupacional envolvidos nos exames, e com fornecedores de tecnologia
				que apoiam a operação desta plataforma, sempre sob obrigação contratual de confidencialidade.
				Também podemos compartilhar informações quando houver determinação legal ou judicial.
				Não vendemos nem cedemos seus dados pessoais a terceiros.
			</p>
		),
	},
	{
		title: 'Como protegemos suas informações',
		content: (
			<p>
				Adotamos medidas técnicas e administrativas para proteger seus dados contra acessos não autorizados,
				perda ou alteração indevida, incluindo controle de acesso por autenticação, restrição de acesso
				conforme a necessidade de cada perfil, tráfego criptografado e registros de auditoria das operações
				realizadas na plataforma.
			</p>
		),
	},
	{
		title: 'Seus direitos como titular',
		content: (
			<>
				<p>A LGPD garante a você, a qualquer momento, o direito de:</p>
				<ul>
					<li>confirmar a existência de tratamento e acessar seus dados;</li>
					<li>corrigir dados incompletos, inexatos ou desatualizados;</li>
					<li>solicitar anonimização, bloqueio ou eliminação de dados desnecessários ou excessivos;</li>
					<li>pedir a portabilidade dos dados e informações sobre com quem foram compartilhados;</li>
					<li>revogar consentimento, quando o tratamento se basear nessa hipótese.</li>
				</ul>
				<p>
					Alguns pedidos podem ser limitados quando a manutenção do dado for exigida por obrigação legal.
					Nesses casos, explicaremos o motivo da recusa.
				</p>
			</>
		),
	},
	{
		title: 'Cookies e dados de navegação',
		content: (
			<p>
				Utilizamos apenas cookies e armazenamento local essenciais ao funcionamento da plataforma, como a
				manutenção da sua sessão autenticada e preferências básicas de uso. Não empregamos cookies de
				publicidade ou de rastreamento entre sites.
			</p>
		),
	},
	{
		title: 'Alterações desta política',
		content: (
			<p>
				Esta política pode ser atualizada para refletir mudanças legais, técnicas ou de processo. A data da
				última revisão é sempre indicada no início desta página; recomendamos consultá-la periodicamente.
			</p>
		),
	},
	{
		title: 'Como falar com a gente',
		content: (
			<p>
				Para exercer seus direitos ou esclarecer dúvidas sobre o tratamento dos seus dados, entre em contato
				com o nosso Encarregado pela Proteção de Dados pelo e-mail <b>{DPO_EMAIL}</b>. Responderemos no
				menor prazo possível, observados os limites da legislação aplicável.
			</p>
		),
	},
];

const PrivacyPolicy: FC = () => {
	return (
		<ThemeProvider theme={lightTheme}>
			<div className="privacy-policy-page">
				<Box className="policy-container" component="main">
					<header className="policy-header">
						<img src={logo} alt="Logo da empresa" className="policy-logo" />
						<Typography variant="h4" component="h1">
							Política de Privacidade e LGPD
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Última atualização: {LAST_UPDATE}
						</Typography>
					</header>
					<Box className="policy-highlight">
						<InfoOutlined fontSize="small" />
						<Typography variant="body2">
							Os dados pessoais e os arquivos enviados nesta plataforma são tratados exclusivamente
							para <b>fins admissionais</b> e mantidos <b>por tempo limitado</b>, sendo eliminados
							após o cumprimento dessa finalidade e dos prazos legais de guarda.
						</Typography>
					</Box>
					{sections.map((section, index) => (
						<section className="policy-section" key={section.title}>
							<Box className="section-title">
								<span className="section-number">{index + 1}</span>
								<Typography variant="h6" component="h2">
									{section.title}
								</Typography>
							</Box>
							<div className="section-body">{section.content}</div>
						</section>
					))}
					<Divider />
					<Box className="policy-footer">
						<Typography variant="body2">
							{CONTROLLER_NAME} — comprometida com a proteção dos seus dados pessoais.
						</Typography>
					</Box>
				</Box>
			</div>
		</ThemeProvider>
	);
};

export default PrivacyPolicy;